import { c as _c } from "react/compiler-runtime";
import { TokenIcon } from "@/components/ui/token-icon";
import { formatTokenAmount } from "@/components/ui/workflow-step-row";
import { ChainBadge } from "@/components/ui/workflow-step-row/components/ChainBadge";
import { containsTemplateVariable } from "@/components/ui/workflow-step-row/components/VariablePill";
import { getNativeTokenDecimals } from "@/lib/chains";
import { camelToWords } from "@/utils/string";
import { extractTemplateVariableNames, formatDelayTime, formatDisplayUrl, TemplateMarkdownPreview } from "./ActionNodeValueFormat";
import { resolveSlackConversationLabel, type SlackChannel } from "./form-fields/slackChannels";
import { createPrimarySummary, createStackedSummary, createTransferSummary, type NodeSummaryResult, QuickBooksAccountBadge, SlackChannelBadge, SummaryAddress, SummaryAmount, SummaryAux, SummaryBadge, SummaryChain, type SummaryContext, SummaryQuickBooksAccount, SummarySlackChannel, SummaryStack, SummaryTelegramChat, SummaryText, SlackUserBadge, TelegramChatBadge, TextBadge } from "./node-summaries/common";

/** Slack action schemas use "channel"; legacy payloads may use "conversation". */
const SLACK_CHANNEL_KEYS = ["channel", "conversation"] as const;

/** Render message text with markdown formatting + variable highlights, falling back to plain SummaryText. */
function messageContent(value: unknown, getNodeName?: (nodeId: string) => string | undefined) {
  if (typeof value === "string" && value.trim()) {
    return <TemplateMarkdownPreview content={value} getNodeName={getNodeName} />;
  }
  return <SummaryText value={value} getNodeName={getNodeName} fallback="message" />;
}
function formatTriggerTypeLabel(triggerType: unknown): string {
  if (typeof triggerType !== "string" || !triggerType.trim()) return "event";
  const customLabels: Record<string, string> = {
    "token-price-cexes": "price change",
    "evm-log": "on-chain event",
    "erc20-receive": "token transfer",
    "erc20-send": "token transfer",
    "eth-receive": "native transfer",
    "eth-send": "native transfer",
    schedule: "schedule",
    "slack-new-reaction-added": "Slack reaction",
    "slack-new-keyword-mention": "Slack keyword mention",
    "slack-new-message-in-channels": "Slack message",
    "slack-new-interaction-event": "Slack interaction"
  };
  return customLabels[triggerType] || camelToWords(triggerType).toLowerCase();
}

/** Renders a badge for a Slack conversations array: single channel name or "N channels". */
function SlackConversationsBadge(t0) {
  const $ = _c(6);
  const {
    conversations,
    slackChannelsMap
  } = t0;
  if (conversations.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== conversations[0] || $[1] !== conversations.length || $[2] !== slackChannelsMap) {
    t1 = conversations.length === 1 && typeof conversations[0] === "string" ? resolveSlackConversationLabel(conversations[0], slackChannelsMap) || "Slack channel" : `${conversations.length} channels`;
    $[0] = conversations[0];
    $[1] = conversations.length;
    $[2] = slackChannelsMap;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <SummaryBadge>{t1}</SummaryBadge>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function summarizeConditionDsl(input: unknown): string {
  if (!input || typeof input !== "object") return "configured condition";
  const condition = input as Record<string, unknown>;
  if (Array.isArray(condition.$and)) {
    const parts = condition.$and.map(summarizeConditionDsl).filter(Boolean);
    return parts.length > 0 ? parts.join(" and ") : "configured condition";
  }
  if (Array.isArray(condition.$or)) {
    const parts = condition.$or.map(summarizeConditionDsl).filter(Boolean);
    return parts.length > 0 ? parts.join(" or ") : "configured condition";
  }
  const [left, rawRight] = Object.entries(condition)[0] || [];
  if (!left || !rawRight || typeof rawRight !== "object" || Array.isArray(rawRight)) {
    return "configured condition";
  }
  const [operator, right] = Object.entries(rawRight as Record<string, unknown>)[0] || [];
  if (!operator) return "configured condition";
  const opLabels: Record<string, string> = {
    $eq: "is",
    $ne: "is not",
    $gt: "is greater than",
    $gte: "is at least",
    $lt: "is less than",
    $lte: "is at most",
    $in: "is one of",
    $nin: "is not one of"
  };
  const formattedRight = Array.isArray(right) ? right.map(item => String(item)).join(", ") : typeof right === "object" ? JSON.stringify(right) : String(right);
  return `${left} ${opLabels[operator] || operator} ${formattedRight}`;
}
export function getActionNodeSummary(type: string | undefined, payload: Record<string, unknown> | undefined, context: SummaryContext): NodeSummaryResult | null {
  if (!type || !payload) return null;
  const {
    getNodeName,
    slackChannelsMap,
    telegramChatsMap,
    quickBooksAccountsMap
  } = context;
  if (type === "send-native-token" || type === "send-sol") {
    return createTransferSummary({
      verb: "Transfer",
      amount: payload.amount,
      decimals: getNativeTokenDecimals(payload.chainId as number | undefined),
      asset: <>{type === "send-sol" ? "SOL" : "native token"}</>,
      chainId: payload.chainId,
      recipient: payload.recipientAddress,
      chainInfoMap: context.chainInfoMap
    });
  }
  if (type === "send-erc20-token") {
    return createTransferSummary({
      verb: "Transfer",
      amount: payload.amount,
      decimals: context.tokenInfo?.decimals,
      asset: <>{context.tokenInfo?.symbol || "token"}</>,
      chainId: payload.chainId,
      recipient: payload.recipientAddress,
      chainInfoMap: context.chainInfoMap
    });
  }
  if (type === "anyspend-disperse-native-token" || type === "anyspend-disperse-erc20-token") {
    const isErc20 = type === "anyspend-disperse-erc20-token";
    const recipients = Array.isArray(payload.recipients) ? payload.recipients : [];
    const amounts = Array.isArray(payload.amounts) ? payload.amounts : [];
    const decimals = isErc20 ? payload.tokenDecimals != null ? Number(payload.tokenDecimals) : 18 : getNativeTokenDecimals(payload.chainId as number | undefined);
    const symbol = isErc20 ? payload.tokenSymbol ? String(payload.tokenSymbol) : "tokens" : "ETH";

    // Sum all amounts for total
    let totalFormatted: string | null = null;
    if (amounts.length > 0 && amounts.every((a: unknown) => typeof a === "string" && /^\d+$/.test(a) || typeof a === "number")) {
      try {
        const total = amounts.reduce((sum: bigint, a: unknown) => sum + BigInt(a as string), 0n);
        totalFormatted = formatTokenAmount(total.toString(), decimals);
      } catch {
        // skip if BigInt fails
      }
    }
    return createPrimarySummary({
      summary: <>
          Disperse{" "}
          {totalFormatted ? <SummaryBadge>
              {totalFormatted} {symbol}
            </SummaryBadge> : <SummaryBadge>{symbol}</SummaryBadge>}{" "}
          to{" "}
          {recipients.length > 0 ? <SummaryBadge>
              {recipients.length} recipient{recipients.length === 1 ? "" : "s"}
            </SummaryBadge> : <SummaryBadge>recipients</SummaryBadge>}{" "}
          on{" "}
          <SummaryBadge>
            <SummaryChain chainId={payload.dstChainId != null ? Number(payload.dstChainId) : undefined} chainInfoMap={context.chainInfoMap} />
          </SummaryBadge>
        </>,
      consumedKeys: ["recipients", "amounts", "dstChainId", "dstTokenAddress", "srcChainId", "srcTokenAddress", "tokenSymbol", "tokenDecimals", "partnerId", "clientReferenceId"]
    });
  }
  if (type === "get-balance") {
    const chainId = typeof payload.chainId === "number" ? payload.chainId : Number(payload.chainId);
    const chain = Number.isFinite(chainId) ? context.chainInfoMap?.get(chainId) : undefined;
    const tokenLabel = context.tokenInfo?.name || context.tokenInfo?.symbol || "token";
    return createPrimarySummary({
      summary: <>
          Fetch balance of{" "}
          <SummaryBadge>
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <TokenIcon logoUrl={context.tokenInfo?.metadata?.logoURI} symbol={context.tokenInfo?.symbol || tokenLabel} chainId={context.tokenInfo?.chainId} tokenAddress={context.tokenInfo?.address} size={14} />
              <span className="truncate">{tokenLabel}</span>
            </span>
          </SummaryBadge>{" "}
          on{" "}
          {chain ? <ChainBadge chain={chain} size="sm" variant="summary" /> : <SummaryBadge>
              <SummaryChain chainId={payload.chainId} chainInfoMap={context.chainInfoMap} />
            </SummaryBadge>}{" "}
          on <SummaryAddress value={payload.accountAddress} fallback="wallet" />
        </>,
      consumedKeys: ["tokenAddress", "chainId", "accountAddress", "includeMetadata"],
      auxiliary: payload.includeMetadata ? <SummaryAux>Including token metadata</SummaryAux> : null
    });
  }
  if (type === "send-discord-message") {
    return createStackedSummary({
      verb: "Send",
      content: messageContent(payload.message, getNodeName),
      destination: <SummaryBadge>Discord</SummaryBadge>,
      consumedKeys: ["message"]
    });
  }

  // Only resolve channel/conversation for slack-* action types — the generic fallback
  // below is reached by non-Slack actions that may also use these keys differently.
  const slackChannel = type.startsWith("slack-") ? payload.channel ?? payload.conversation : undefined;
  if (type === "slack-send-message" || type === "slack-reply-to-message" || type === "slack-update-message") {
    return createStackedSummary({
      verb: "Send",
      content: messageContent(payload.text, getNodeName),
      destination: <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} />,
      consumedKeys: [...SLACK_CHANNEL_KEYS, "text", "thread_ts"],
      auxiliary: type === "slack-reply-to-message" && payload.thread_ts ? <SummaryAux>Replying in an existing thread</SummaryAux> : null
    });
  }
  if (type === "slack-send-block-kit-message") {
    return createStackedSummary({
      verb: "Send",
      content: <SummaryText value={payload.text} getNodeName={getNodeName} fallback="Block Kit message" />,
      destination: <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} />,
      consumedKeys: [...SLACK_CHANNEL_KEYS, "text", "blocks", "thread_ts", "thread_broadcast"],
      auxiliary: <SummaryAux>Using Block Kit layout</SummaryAux>
    });
  }
  if (type === "slack-send-message-to-user-or-group") {
    const users = Array.isArray(payload.users) ? payload.users.filter(Boolean) : [];
    const destination = slackChannel ? <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} /> : users.length > 1 ? <SummaryBadge>{`${users.length} Slack users`}</SummaryBadge> : users.length === 1 ? <SummaryBadge>{users[0]}</SummaryBadge> : <SummaryBadge>Slack recipient</SummaryBadge>;
    return createStackedSummary({
      verb: "Send",
      content: messageContent(payload.text, getNodeName),
      destination,
      consumedKeys: ["text", ...SLACK_CHANNEL_KEYS, "users", "thread_ts", "thread_broadcast"]
    });
  }
  if (type === "slack-upload-file") {
    return createPrimarySummary({
      summary: <>
          Upload <TextBadge value={payload.content} getNodeName={getNodeName} fallback="file" /> to{" "}
          <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} />
        </>,
      consumedKeys: [...SLACK_CHANNEL_KEYS, "content", "initialComment"],
      auxiliary: payload.initialComment ? <SummaryAux>
          With comment <SummaryText value={payload.initialComment} getNodeName={getNodeName} fallback="" />
        </SummaryAux> : null
    });
  }
  if (type === "slack-add-reaction") {
    return createPrimarySummary({
      summary: <>
          Add <SummaryBadge>{String(payload.icon_emoji || "reaction")}</SummaryBadge> in{" "}
          <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} />
        </>,
      consumedKeys: [...SLACK_CHANNEL_KEYS, "timestamp", "icon_emoji"]
    });
  }
  if (type === "slack-create-reminder") {
    return createPrimarySummary({
      summary: <>
          Create Slack reminder for <TextBadge value={payload.user} getNodeName={getNodeName} fallback="current user" />
        </>,
      consumedKeys: ["user", "text", "timestamp", "team_id"],
      auxiliary: <SummaryAux>
          <SummaryText value={payload.text} getNodeName={getNodeName} fallback="Reminder" /> ·{" "}
          <SummaryText value={payload.timestamp} getNodeName={getNodeName} fallback="scheduled time" />
        </SummaryAux>
    });
  }
  if (type === "slack-create-channel") {
    return createPrimarySummary({
      summary: <>
          Create <SummaryBadge>{payload.isPrivate ? "private" : "public"}</SummaryBadge> Slack channel{" "}
          <TextBadge value={payload.channelName} getNodeName={getNodeName} fallback="channel" />
        </>,
      consumedKeys: ["channelName", "isPrivate"]
    });
  }
  if (type === "slack-archive-channel") {
    return createPrimarySummary({
      summary: <>
          Archive <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} />
        </>,
      consumedKeys: [...SLACK_CHANNEL_KEYS]
    });
  }
  if (type === "slack-invite-user-to-channel" || type === "slack-kick-user") {
    const verb = type === "slack-invite-user-to-channel" ? "Invite" : "Remove";
    const consumedKeys = [...SLACK_CHANNEL_KEYS, "user"];
    return createPrimarySummary({
      summary: <>
          {verb} <TextBadge value={payload.user} getNodeName={getNodeName} fallback="user" />{" "}
          {type === "slack-invite-user-to-channel" ? "to" : "from"}{" "}
          <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} />
        </>,
      consumedKeys
    });
  }
  if (type === "slack-set-channel-topic" || type === "slack-set-channel-description") {
    const fieldKey = type === "slack-set-channel-topic" ? "topic" : "purpose";
    const verb = type === "slack-set-channel-topic" ? "Set topic for" : "Set description for";
    return createPrimarySummary({
      summary: <>
          {verb} <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} />
        </>,
      consumedKeys: [...SLACK_CHANNEL_KEYS, fieldKey],
      auxiliary: <SummaryAux>
          <SummaryText value={payload[fieldKey]} getNodeName={getNodeName} fallback="" />
        </SummaryAux>
    });
  }
  if (type === "slack-delete-message") {
    return createPrimarySummary({
      summary: <>
          Delete message in <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} />
        </>,
      consumedKeys: [...SLACK_CHANNEL_KEYS, "timestamp", "as_user"]
    });
  }
  if (type === "slack-delete-file") {
    return createPrimarySummary({
      summary: <>
          Delete file <TextBadge value={payload.file} getNodeName={getNodeName} fallback="file" /> from{" "}
          <SlackChannelBadge value={slackChannel} slackChannelsMap={slackChannelsMap} />
        </>,
      consumedKeys: [...SLACK_CHANNEL_KEYS, "file"]
    });
  }
  if (type === "slack-set-status") {
    return createPrimarySummary({
      summary: <>
          Set Slack status to <TextBadge value={payload.statusText} getNodeName={getNodeName} fallback="status" />
        </>,
      consumedKeys: ["statusText", "statusEmoji", "statusExpiration"],
      auxiliary: <SummaryAux>
          {payload.statusEmoji ? `${String(payload.statusEmoji)} · ` : null}
          <SummaryText value={payload.statusExpiration} getNodeName={getNodeName} fallback="no expiration" />
        </SummaryAux>
    });
  }
  if (type === "slack-update-profile") {
    const targetUser = payload.user || "current user";
    const fields = ["displayName", "firstName", "lastName", "phone", "pronouns", "title", "email"].filter(key => payload[key]);
    return createPrimarySummary({
      summary: <>
          Update Slack profile for <TextBadge value={targetUser} getNodeName={getNodeName} fallback="current user" />
        </>,
      consumedKeys: ["displayName", "firstName", "lastName", "phone", "pronouns", "title", "email", "user"],
      auxiliary: fields.length > 0 ? <SummaryAux>Fields: {fields.map(field => camelToWords(field)).join(", ")}</SummaryAux> : null
    });
  }
  if (type === "slack-update-group-members") {
    const usersToAdd = Array.isArray(payload.usersToAdd) ? payload.usersToAdd.length : 0;
    const usersToRemove = Array.isArray(payload.usersToRemove) ? payload.usersToRemove.length : 0;
    return createPrimarySummary({
      summary: <>
          Update Slack user group <TextBadge value={payload.userGroup} getNodeName={getNodeName} fallback="group" />
        </>,
      consumedKeys: ["userGroup", "usersToAdd", "usersToRemove", "team"],
      auxiliary: <SummaryAux>
          {usersToAdd > 0 ? `Add ${usersToAdd}` : "Add 0"} ·{" "}
          {usersToRemove > 0 ? `Remove ${usersToRemove}` : "Remove 0"}
        </SummaryAux>
    });
  }
  if (type === "send-slack-message") {
    return createStackedSummary({
      verb: "Send",
      content: messageContent(payload.message, getNodeName),
      destination: <SummaryBadge>Slack</SummaryBadge>,
      consumedKeys: ["message"]
    });
  }
  if (type === "send-email" || type === "gmail-send-email" || type === "gmail-create-draft") {
    return {
      summary: <SummaryStack verb={type === "gmail-create-draft" ? "Draft Email" : "Send Email"} content={<SummaryText value={payload.subject} getNodeName={getNodeName} fallback="subject" />} destination={<TextBadge value={payload.to} getNodeName={getNodeName} fallback="recipient" />} />,
      consumedKeys: ["to", "subject", "text", "html", "body"],
      auxiliary: payload.replyTo || payload.cc || payload.bcc ? <SummaryAux>
            {payload.replyTo ? <>
                Reply to <SummaryText value={payload.replyTo} getNodeName={getNodeName} fallback="" />
              </> : null}
            {payload.replyTo && (payload.cc || payload.bcc) ? " · " : null}
            {payload.cc ? <>
                CC <SummaryText value={payload.cc} getNodeName={getNodeName} fallback="" />
              </> : null}
            {payload.cc && payload.bcc ? " · " : null}
            {payload.bcc ? <>
                BCC <SummaryText value={payload.bcc} getNodeName={getNodeName} fallback="" />
              </> : null}
          </SummaryAux> : null
    };
  }
  if (type === "quickbooks-create-purchase" || type === "quickbooks-sandbox-create-purchase") {
    return createStackedSummary({
      verb: "Record",
      content: <>
          <TextBadge value={payload.amount} getNodeName={getNodeName} fallback="purchase" /> purchase
        </>,
      destination: <QuickBooksAccountBadge value={payload.expenseAccountId || payload.expenseAccountName} quickBooksAccountsMap={quickBooksAccountsMap} fallback="expense account" />,
      consumedKeys: ["amount", "currency", "description", "paymentType", "accountId", "accountName", "expenseAccountId", "expenseAccountName"],
      auxiliary: <SummaryAux>
          Paid via{" "}
          <SummaryQuickBooksAccount value={payload.accountId || payload.accountName} quickBooksAccountsMap={quickBooksAccountsMap} fallback="payment account" />
          {payload.paymentType ? ` · ${String(payload.paymentType)}` : null}
          {payload.description ? <>
              {" · "}
              <SummaryText value={payload.description} getNodeName={getNodeName} fallback="" />
            </> : null}
        </SummaryAux>
    });
  }
  if (type === "send-telegram-message") {
    return createStackedSummary({
      verb: "Send",
      content: messageContent(payload.text, getNodeName),
      destination: <TelegramChatBadge value={payload.chatId} telegramChatsMap={telegramChatsMap} />,
      consumedKeys: ["chatId", "text"],
      auxiliary: payload.reply_to_message_id ? <SummaryAux>Replying in an existing thread</SummaryAux> : null
    });
  }
  if (type === "swapkit-swap") {
    const parseAssetSymbol = (v: unknown): string => {
      if (typeof v !== "string" || !v || v.includes("{{")) return "token";
      return v.split(".").pop()?.split("-")[0] || "token";
    };
    return createStackedSummary({
      verb: "Swap",
      content: <>
          <SummaryText value={payload.sellAmount} getNodeName={getNodeName} fallback="amount" />{" "}
          {parseAssetSymbol(payload.sellAsset)} for {parseAssetSymbol(payload.buyAsset)}
        </>,
      destination: <SummaryAddress value={payload.recipient} fallback="recipient" />,
      consumedKeys: ["sellAsset", "buyAsset", "sellAmount", "recipient", "srcChainId", "slippage", "providers"]
    });
  }
  if (type === "format") {
    const templateVars = extractTemplateVariableNames(payload.data);
    const visibleTemplateVars = templateVars.slice(0, 3);
    const remainingTemplateVarCount = templateVars.length - visibleTemplateVars.length;
    return createPrimarySummary({
      summary: <>
          Format{" "}
          {templateVars.length > 0 ? <>
              <SummaryBadge>
                {templateVars.length} field{templateVars.length === 1 ? "" : "s"}
              </SummaryBadge>{" "}
              using a template
            </> : <>
              <SummaryText value={payload.data} getNodeName={getNodeName} fallback="data" /> using a template
            </>}
        </>,
      consumedKeys: ["data", "template", "separator"],
      auxiliary: templateVars.length > 0 ? <SummaryAux>
            <span className="inline-flex flex-wrap items-center gap-1">
              {visibleTemplateVars.map(varName => <SummaryBadge key={varName}>{varName}</SummaryBadge>)}
              {remainingTemplateVarCount > 0 ? <span>+{remainingTemplateVarCount} more</span> : null}
            </span>
          </SummaryAux> : null
    });
  }
  const method = payload.method || payload.httpMethod;
  const url = payload.url || payload.endpoint || payload.webhookUrl;
  if (typeof method === "string" && method || typeof url === "string" && url) {
    return {
      summary: <>
          {typeof method === "string" && method ? method.toUpperCase() : "Call"}{" "}
          <SummaryBadge>{typeof url === "string" && url ? formatDisplayUrl(url) : "endpoint"}</SummaryBadge>
        </>,
      consumedKeys: ["method", "httpMethod", "url", "endpoint", "webhookUrl", "body", "json", "data"]
    };
  }
  const message = payload.message || payload.text;
  const destination = payload.to || payload.recipient || payload.channel || payload.chatId || payload.conversation;
  if (message && !destination) {
    return createPrimarySummary({
      summary: <SummaryText value={message} getNodeName={getNodeName} fallback="message" />,
      consumedKeys: ["message", "text"]
    });
  }
  if (message || destination) {
    return {
      summary: <SummaryStack verb="Send" content={<SummaryText value={message} getNodeName={getNodeName} fallback="message" />} destination={<SummaryBadge>
              {slackChannel ? <SummarySlackChannel value={slackChannel} slackChannelsMap={slackChannelsMap} /> : payload.chatId ? <SummaryTelegramChat value={payload.chatId} telegramChatsMap={telegramChatsMap} /> : <SummaryText value={destination} getNodeName={getNodeName} fallback="destination" />}
            </SummaryBadge>} />,
      consumedKeys: ["message", "text", "to", "recipient", "chatId", ...SLACK_CHANNEL_KEYS]
    };
  }
  return null;
}
export function getTriggerNodeSummary(type: string | undefined, payload: Record<string, unknown> | undefined, context: SummaryContext): NodeSummaryResult | null {
  if (!type || !payload) return null;
  const {
    chainInfoMap,
    tokenInfo,
    telegramChatsMap,
    slackChannelsMap,
    slackUsersMap
  } = context;
  if (type === "telegram-channel") {
    return {
      summary: <>
          Trigger when a new Telegram message arrives in{" "}
          <SummaryBadge>
            <SummaryTelegramChat value={payload.chatId} telegramChatsMap={telegramChatsMap} />
          </SummaryBadge>
        </>,
      consumedKeys: ["chatId"]
    };
  }
  if (type === "slack-new-message-in-channels") {
    const conversations = Array.isArray(payload.conversations) ? payload.conversations : [];
    return {
      summary: <>
          Trigger when a new Slack message arrives{" "}
          {conversations.length > 0 ? <>
              in <SlackConversationsBadge conversations={conversations} slackChannelsMap={slackChannelsMap} />
            </> : <>in Slack</>}
        </>,
      consumedKeys: ["conversations"]
    };
  }
  if (type === "slack-mentions") {
    return {
      summary: <>
          Trigger when the Slack bot is mentioned{" "}
          <SummaryBadge>
            <SummarySlackChannel value={payload.channelId} slackChannelsMap={slackChannelsMap} />
          </SummaryBadge>
        </>,
      consumedKeys: ["channelId", "botToken"]
    };
  }
  if (type === "email-new-email-received" || type === "gmail-new-email" || type === "gmail-new-email-received") {
    return {
      summary: <>Trigger when a new email arrives</>,
      consumedKeys: ["query", "q", "labelIds", "labels"]
    };
  }
  if (type === "email-new-email-matching-criteria" || type === "gmail-new-email-matching-search") {
    const hasFilter = !!payload.fromFilter || !!payload.subjectFilter || !!payload.query || !!payload.q;
    return {
      summary: <>
          Trigger when a new email matches{" "}
          <SummaryBadge>
            <SummaryText value={payload.q || payload.query || payload.fromFilter || payload.subjectFilter} getNodeName={context.getNodeName} fallback="your filter" />
          </SummaryBadge>
        </>,
      consumedKeys: ["query", "q", "labelIds", "labels", "fromFilter", "subjectFilter"],
      auxiliary: hasFilter && payload.fromFilter && payload.subjectFilter ? <SummaryAux>
            From <SummaryText value={payload.fromFilter} getNodeName={context.getNodeName} fallback="" /> · Subject{" "}
            <SummaryText value={payload.subjectFilter} getNodeName={context.getNodeName} fallback="" />
          </SummaryAux> : null
    };
  }
  if (type === "erc20-receive") {
    return {
      summary: <>
          Trigger when <SummaryBadge>{tokenInfo?.symbol || "ERC-20 tokens"}</SummaryBadge> are received by{" "}
          <SummaryBadge>
            <SummaryAddress value={payload.toAddress} fallback="wallet" />
          </SummaryBadge>{" "}
          on{" "}
          <SummaryBadge>
            <SummaryChain chainId={payload.chainId} chainInfoMap={chainInfoMap} />
          </SummaryBadge>
        </>,
      consumedKeys: ["chainId", "toAddress", "tokenAddress"]
    };
  }
  if (type === "erc20-send") {
    return {
      summary: <>
          Trigger when <SummaryBadge>{tokenInfo?.symbol || "ERC-20 tokens"}</SummaryBadge> are sent from{" "}
          <SummaryBadge>
            <SummaryAddress value={payload.fromAddress} fallback="wallet" />
          </SummaryBadge>{" "}
          on{" "}
          <SummaryBadge>
            <SummaryChain chainId={payload.chainId} chainInfoMap={chainInfoMap} />
          </SummaryBadge>
        </>,
      consumedKeys: ["chainId", "fromAddress", "tokenAddress"]
    };
  }
  if (type === "eth-receive") {
    return {
      summary: <>
          Trigger when native tokens are received by{" "}
          <SummaryBadge>
            <SummaryAddress value={payload.walletAddress} fallback="wallet" />
          </SummaryBadge>{" "}
          on{" "}
          <SummaryBadge>
            <SummaryChain chainId={payload.chainId != null ? Number(payload.chainId) : undefined} chainInfoMap={chainInfoMap} />
          </SummaryBadge>
        </>,
      consumedKeys: ["chainId", "walletAddress"]
    };
  }
  if (type === "eth-send") {
    return {
      summary: <>
          Trigger when native tokens are sent from{" "}
          <SummaryBadge>
            <SummaryAddress value={payload.walletAddress} fallback="wallet" />
          </SummaryBadge>{" "}
          on{" "}
          <SummaryBadge>
            <SummaryChain chainId={payload.chainId != null ? Number(payload.chainId) : undefined} chainInfoMap={chainInfoMap} />
          </SummaryBadge>
        </>,
      consumedKeys: ["chainId", "walletAddress"]
    };
  }
  if (type === "anyspend") {
    const hasAmount = payload.amount !== undefined && payload.amount !== "";
    return {
      summary: <>
          Trigger when{" "}
          {hasAmount ? <>
              <SummaryBadge>
                <SummaryAmount value={payload.amount} decimals={tokenInfo?.decimals} fallback="a payment" />
                {tokenInfo?.symbol ? ` ${tokenInfo.symbol}` : ""}
              </SummaryBadge>{" "}
              is paid
            </> : <>a payment is received</>}{" "}
          to{" "}
          <SummaryBadge>
            <SummaryAddress value={payload.recipientAddress} fallback="wallet" />
          </SummaryBadge>{" "}
          on{" "}
          <SummaryBadge>
            <SummaryChain chainId={payload.chainId != null ? Number(payload.chainId) : undefined} chainInfoMap={chainInfoMap} />
          </SummaryBadge>
        </>,
      consumedKeys: ["chainId", "recipientAddress", "tokenAddress", "amount", "enablePaymentLink"]
    };
  }

  // --- Cronjob / Recurring Schedule ---
  // Note: rrule is NOT consumed so TriggerNode's formatRRule() fallback renders the readable schedule
  if (type === "cronjob") {
    return {
      summary: <>Trigger on a recurring schedule</>,
      consumedKeys: []
    };
  }

  // --- Token Price ---
  if (type === "token-price-cexes") {
    const op = payload.operation as string | undefined;
    const priceNum = payload.targetPrice != null && payload.targetPrice !== "" ? Number(payload.targetPrice) : NaN;
    const price = Number.isFinite(priceNum) ? priceNum : undefined;
    const asset = payload.asset as string | undefined;
    const opLabel: Record<string, string> = {
      "<": "falls below",
      ">": "rises above",
      "<=": "falls to or below",
      ">=": "rises to or above",
      "==": "equals"
    };
    return {
      summary: <>
          Trigger when{" "}
          {asset && !containsTemplateVariable(asset) ? <SummaryBadge>
              <span className="inline-flex items-center gap-1.5">
                <TokenIcon symbol={asset} size={14} enableStockTickers />
                <span>{asset}</span>
              </span>
            </SummaryBadge> : <TextBadge value={asset} fallback="asset" />}{" "}
          {opLabel[op || ""] || "reaches"}{" "}
          <SummaryBadge>${price !== undefined ? price.toLocaleString() : "target"}</SummaryBadge>
        </>,
      consumedKeys: ["asset", "targetPrice", "operation"]
    };
  }

  // --- Polymarket triggers ---
  if (type === "polymarket-user-bet") {
    return {
      summary: <>
          Trigger when{" "}
          <SummaryBadge>
            <SummaryAddress value={payload.user} fallback="a trader" />
          </SummaryBadge>{" "}
          places a bet on Polymarket
        </>,
      consumedKeys: ["user"]
    };
  }
  if (type === "polymarket-market-trade") {
    return {
      summary: <>
          Trigger when a trade occurs on{" "}
          <SummaryBadge>
            {typeof payload.marketId === "string" && payload.marketId ? payload.marketId.length > 20 ? `${payload.marketId.slice(0, 8)}...` : payload.marketId : "a Polymarket market"}
          </SummaryBadge>
        </>,
      consumedKeys: ["marketId"]
    };
  }
  if (type === "polymarket-new-market") {
    const keywords = Array.isArray(payload.keywords) ? payload.keywords : [];
    return {
      summary: <>
          Trigger when a new Polymarket market is listed
          {keywords.length > 0 && <>
              {" "}
              matching <SummaryBadge>{keywords.join(", ")}</SummaryBadge>
            </>}
        </>,
      consumedKeys: ["keywords"]
    };
  }
  if (type === "polymarket-market-close") {
    return {
      summary: <>
          Trigger when{" "}
          {typeof payload.marketId === "string" && payload.marketId ? <>
              Polymarket market{" "}
              <SummaryBadge>
                {payload.marketId.length > 20 ? `${payload.marketId.slice(0, 8)}...` : payload.marketId}
              </SummaryBadge>
            </> : <>a Polymarket market</>}{" "}
          closes
        </>,
      consumedKeys: ["marketId"]
    };
  }

  // --- Shopify triggers ---
  if (type === "shopify-order-created" || type === "shopify-order-paid" || type === "shopify-inventory-level-updated") {
    const eventLabel = type === "shopify-order-created" ? "a new order is created" : type === "shopify-order-paid" ? "an order is paid" : "inventory levels are updated";
    return {
      summary: <>
          Trigger when {eventLabel}
          {typeof payload.shopDomain === "string" && payload.shopDomain && <>
              {" "}
              on <SummaryBadge>{payload.shopDomain}</SummaryBadge>
            </>}
        </>,
      consumedKeys: ["shopDomain"]
    };
  }

  // --- Stripe ---
  if (type === "stripe-payment-receive") {
    return {
      summary: <>Trigger when a Stripe payment is received</>,
      consumedKeys: []
    };
  }

  // --- Slack triggers (remaining types) ---
  if (type === "slack-new-reaction-added") {
    const conversations = Array.isArray(payload.conversations) ? payload.conversations : [];
    return {
      summary: <>
          Trigger when a reaction is added
          {conversations.length > 0 && <>
              {" "}
              in <SlackConversationsBadge conversations={conversations} slackChannelsMap={slackChannelsMap} />
            </>}
        </>,
      consumedKeys: ["conversations"]
    };
  }
  if (type === "slack-new-keyword-mention") {
    const conversations = Array.isArray(payload.conversations) ? payload.conversations : [];
    return {
      summary: <>
          Trigger when <SummaryBadge>{typeof payload.keyword === "string" ? payload.keyword : "keyword"}</SummaryBadge>{" "}
          is mentioned in Slack
          {conversations.length > 0 && <>
              {" "}
              in <SlackConversationsBadge conversations={conversations} slackChannelsMap={slackChannelsMap} />
            </>}
        </>,
      consumedKeys: ["keyword", "conversations", "ignoreBot"]
    };
  }
  if (type === "slack-new-user-mention") {
    const conversations = Array.isArray(payload.conversations) ? payload.conversations : [];
    return {
      summary: <>
          Trigger when <SlackUserBadge value={payload.user} slackUsersMap={slackUsersMap} /> is mentioned
          {conversations.length > 0 && <>
              {" "}
              in <SlackConversationsBadge conversations={conversations} slackChannelsMap={slackChannelsMap} />
            </>}
        </>,
      consumedKeys: ["user", "conversations", "ignoreBot"]
    };
  }
  if (type === "slack-new-interaction-event") {
    const actionIds = Array.isArray(payload.action_ids) ? payload.action_ids : [];
    const conversations = Array.isArray(payload.conversations) ? payload.conversations : [];
    return {
      summary: <>
          Trigger on a Slack button click or interaction
          {actionIds.length > 0 && <>
              {" "}
              for{" "}
              <SummaryBadge>
                {actionIds.length} action{actionIds.length === 1 ? "" : "s"}
              </SummaryBadge>
            </>}
          {conversations.length > 0 && <>
              {" "}
              in <SlackConversationsBadge conversations={conversations} slackChannelsMap={slackChannelsMap} />
            </>}
        </>,
      consumedKeys: ["action_ids", "conversations"]
    };
  }
  if (type === "slack-new-channel-created") {
    return {
      summary: <>Trigger when a new Slack channel is created</>,
      consumedKeys: []
    };
  }
  if (type === "slack-new-direct-message") {
    return {
      summary: <>Trigger when a new Slack direct message is received</>,
      consumedKeys: ["ignoreBot"]
    };
  }
  if (type === "slack-new-user-added") {
    return {
      summary: <>Trigger when a new user joins the Slack workspace</>,
      consumedKeys: []
    };
  }

  // --- Farcaster triggers ---
  if (type === "farcaster-new-cast") {
    const authorFids = Array.isArray(payload.authorFids) ? payload.authorFids : [];
    const hasPattern = typeof payload.textPattern === "string" && payload.textPattern;
    return {
      summary: <>
          Trigger when a new Farcaster cast is posted
          {authorFids.length > 0 && <>
              {" "}
              by{" "}
              <SummaryBadge>
                {authorFids.length} author{authorFids.length === 1 ? "" : "s"}
              </SummaryBadge>
            </>}
          {hasPattern && <>
              {" "}
              matching <SummaryBadge>{String(payload.textPattern)}</SummaryBadge>
            </>}
        </>,
      consumedKeys: ["authorFids", "textPattern"]
    };
  }
  if (type === "farcaster-new-follower") {
    const targetFids = Array.isArray(payload.targetFids) ? payload.targetFids : [];
    return {
      summary: <>
          Trigger when someone follows
          {targetFids.length > 0 ? <>
              {" "}
              <SummaryBadge>
                {targetFids.length} user{targetFids.length === 1 ? "" : "s"}
              </SummaryBadge>
            </> : <> a Farcaster user</>}
        </>,
      consumedKeys: ["targetFids"]
    };
  }
  if (type === "farcaster-new-reaction") {
    const targetFids = Array.isArray(payload.targetFids) ? payload.targetFids : [];
    return {
      summary: <>
          Trigger when a Farcaster cast
          {targetFids.length > 0 ? <>
              {" "}
              by{" "}
              <SummaryBadge>
                {targetFids.length} user{targetFids.length === 1 ? "" : "s"}
              </SummaryBadge>
            </> : null}{" "}
          receives a reaction
        </>,
      consumedKeys: ["targetFids"]
    };
  }
  return {
    summary: <>Trigger when {camelToWords(type).toLowerCase()}</>,
    consumedKeys: []
  };
}
export function getLogicNodeSummary(type: string | undefined, payload: Record<string, unknown> | undefined, context: SummaryContext): NodeSummaryResult | null {
  if (!type || !payload) return null;
  const {
    getNodeName
  } = context;
  if (type === "if") {
    const summary = summarizeConditionDsl(payload.condition);
    return {
      summary: <>
          Branch when <SummaryText value={summary} getNodeName={getNodeName} fallback="condition matches" />
        </>,
      consumedKeys: ["condition"]
    };
  }
  if (type === "filter") {
    const summary = summarizeConditionDsl(payload.filter);
    return {
      summary: <>
          Keep items from <SummaryText value={payload.array} getNodeName={getNodeName} fallback="list" /> where{" "}
          <SummaryText value={summary} getNodeName={getNodeName} fallback="the filter matches" />
        </>,
      consumedKeys: ["array", "filter"]
    };
  }
  if (type === "for-each") {
    return {
      summary: <>
          Run the next steps for each item in{" "}
          <SummaryText value={payload.array} getNodeName={getNodeName} fallback="the list" />
        </>,
      consumedKeys: ["array"]
    };
  }
  if (type === "pluck") {
    return {
      summary: <>
          Extract{" "}
          <SummaryBadge>
            <SummaryText value={payload.field} getNodeName={getNodeName} fallback="field" />
          </SummaryBadge>{" "}
          from <SummaryText value={payload.array} getNodeName={getNodeName} fallback="each item" />
        </>,
      consumedKeys: ["array", "field"]
    };
  }
  if (type === "wait") {
    return {
      summary: <>
          Wait for <SummaryBadge>{formatTriggerTypeLabel(payload.triggerType)}</SummaryBadge> for{" "}
          <SummaryBadge>
            {typeof payload.timeoutMs === "number" ? formatDelayTime(payload.timeoutMs) : "configured time"}
          </SummaryBadge>
        </>,
      consumedKeys: ["timeoutMs", "triggerType", "triggerConfig"]
    };
  }
  return null;
}